import React from 'react';
import {
  Box, Typography, Button, CircularProgress, alpha, InputAdornment, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import Grid from '@mui/material/Grid'; // Import Grid v2
import { Search, CalendarToday } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { addDays, isValid } from 'date-fns';

import { generateHourOptions } from '../utils/helpers';

const SearchParameters = ({
  date,
  setDate,
  startHour,
  setStartHour,
  endHour,
  setEndHour,
  players,
  setPlayers,
  handleSearch,
  loadingSearch,
  selectedCourses
}) => {
  const theme = useTheme();

  // Generate hour options
  const startHourOptions = generateHourOptions(6, 18); // 6 AM to 6 PM
  const endHourOptions = startHour ?
    generateHourOptions(parseInt(startHour.split(':')[0], 10) + 1, 18) :
    generateHourOptions(7, 18);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }}
    >
      <Typography
        variant="h6"
        component="h2"
        sx={{
          mb: 2,
          fontWeight: 'bold',
          color: theme.palette.primary.dark,
          fontSize: '1.1rem'
        }}
      >
        When would you like to play?
      </Typography>

      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        {/* Date Picker - Left Side */}
        <Grid size={{ xs: 12, sm: 3, md: 2.5 }}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date"
              value={date}
              onChange={(newDate) => {
                if (newDate && isValid(newDate)) {
                  setDate(newDate);
                }
              }}
              minDate={new Date()}
              maxDate={addDays(new Date(), 60)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  size: "small",
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarToday sx={{ fontSize: 18, color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  },
                  sx: {
                    '& .MuiInputBase-root': { fontSize: '0.95rem', borderRadius: 2 },
                    '& .MuiInputLabel-root': { fontSize: '0.95rem' }
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>

        {/* Centered Fields */}
        <Grid container size={{ xs: 12, sm: 6, md: 7 }} spacing={2} justifyContent="center">
          {/* Start Time */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="start-time-label" sx={{ fontSize: '0.95rem' }}>Start</InputLabel>
              <Select
                labelId="start-time-label"
                value={startHour}
                onChange={(e) => setStartHour(e.target.value)}
                label="Start"
                sx={{ borderRadius: 2, fontSize: '0.95rem' }}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 200 } }
                }}
              >
                {startHourOptions.map(option => (
                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.9rem' }}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* End Time */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="end-time-label" sx={{ fontSize: '0.95rem' }}>End</InputLabel>
              <Select
                labelId="end-time-label"
                value={endHour}
                onChange={(e) => setEndHour(e.target.value)}
                label="End"
                sx={{ borderRadius: 2, fontSize: '0.95rem' }}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 200 } }
                }}
              >
                {endHourOptions.map(option => (
                  <MenuItem key={option.value} value={option.value} sx={{ fontSize: '0.9rem' }}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Players */}
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel id="players-label" sx={{ fontSize: '0.95rem' }}>Players</InputLabel>
              <Select
                labelId="players-label"
                value={players}
                onChange={(e) => {
                  setPlayers(Number(e.target.value));
                }}
                label="Players"
                sx={{ borderRadius: 2, fontSize: '0.95rem' }}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 200 } }
                }}
              >
                {[1, 2, 3, 4].map(num => (
                  <MenuItem key={num} value={num} sx={{ fontSize: '0.9rem' }}>
                    {num} {num === 1 ? 'Player' : 'Players'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Search Button - Right Side */}
        <Grid size={{ xs: 12, sm: 3, md: 2.5 }}>
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={selectedCourses.length === 0 || loadingSearch}
            startIcon={loadingSearch ? <CircularProgress size={18} color="inherit" /> : <Search sx={{ fontSize: 18 }} />}
            fullWidth
            sx={{
              py: 0.8,
              fontSize: '0.9rem',
              fontWeight: 'bold',
              borderRadius: 2,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4,
              }
            }}
          >
            {loadingSearch ? 'Searching...' : 'Search Courses'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SearchParameters;